"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useRef } from "react";
const DEFAULT_REFETCH_DEBOUNCE_MS = 1000;
interface UseDebouncedQueryRefetchProps {
  readonly refetch: () => Promise<unknown>;
  readonly isFetching: boolean;
  readonly isFetchingNextPage: boolean;
  readonly debounceMs?: number | undefined;
}
export function useDebouncedQueryRefetch(t0) {
  const $ = _c(22);
  const {
    refetch,
    isFetching,
    isFetchingNextPage,
    debounceMs: t1
  } = t0;
  const debounceMs = t1 === undefined ? DEFAULT_REFETCH_DEBOUNCE_MS : t1;
  const lastRefetchTimeRef = useRef(0);
  const pendingRefetchRef = useRef(false);
  const timeoutRef = useRef(null);
  const isFetchingRef = useRef(isFetching);
  const isFetchingNextPageRef = useRef(isFetchingNextPage);
  let t2;
  let t3;
  if ($[0] !== isFetching || $[1] !== isFetchingNextPage) {
    t2 = () => {
      isFetchingRef.current = isFetching;
      isFetchingNextPageRef.current = isFetchingNextPage;
    };
    t3 = [isFetching, isFetchingNextPage];
    $[0] = isFetching;
    $[1] = isFetchingNextPage;
    $[2] = t2;
    $[3] = t3;
  } else {
    t2 = $[2];
    t3 = $[3];
  }
  useEffect(t2, t3);
  let t4;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = () => {
      if (timeoutRef.current !== null) {
        clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
    $[4] = t4;
  } else {
    t4 = $[4];
  }
  const clearScheduledRefetch = t4;
  let t5;
  if ($[5] !== refetch) {
    t5 = () => {
      lastRefetchTimeRef.current = Date.now();
      pendingRefetchRef.current = false;
      refetch().catch(_temp);
    };
    $[5] = refetch;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  const executeRefetch = t5;
  let t6;
  if ($[7] !== executeRefetch) {
    t6 = delayMs => {
      pendingRefetchRef.current = true;
      timeoutRef.current = setTimeout(() => {
        timeoutRef.current = null;
        if (isFetchingRef.current || isFetchingNextPageRef.current) {
          return;
        }
        executeRefetch();
      }, delayMs);
    };
    $[7] = executeRefetch;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const scheduleRefetch = t6;
  let t7;
  if ($[9] !== debounceMs || $[10] !== executeRefetch || $[11] !== isFetching || $[12] !== isFetchingNextPage || $[13] !== scheduleRefetch) {
    t7 = () => {
      clearScheduledRefetch();
      if (isFetching || isFetchingNextPage) {
        pendingRefetchRef.current = true;
        return;
      }
      const timeSinceLastRefetch = Date.now() - lastRefetchTimeRef.current;
      if (timeSinceLastRefetch < debounceMs) {
        scheduleRefetch(debounceMs - timeSinceLastRefetch);
        return;
      }
      executeRefetch();
    };
    $[9] = debounceMs;
    $[10] = executeRefetch;
    $[11] = isFetching;
    $[12] = isFetchingNextPage;
    $[13] = scheduleRefetch;
    $[14] = t7;
  } else {
    t7 = $[14];
  }
  const requestRefetch = t7;
  let t8;
  let t9;
  if ($[15] !== debounceMs || $[16] !== executeRefetch || $[17] !== isFetching || $[18] !== isFetchingNextPage || $[19] !== scheduleRefetch) {
    t8 = () => {
      if (!isFetching && !isFetchingNextPage && pendingRefetchRef.current) {
        clearScheduledRefetch();
        const timeSinceLastRefetch_0 = Date.now() - lastRefetchTimeRef.current;
        if (timeSinceLastRefetch_0 >= debounceMs) {
          executeRefetch();
        } else {
          scheduleRefetch(debounceMs - timeSinceLastRefetch_0);
        }
      }
      return clearScheduledRefetch;
    };
    t9 = [clearScheduledRefetch, debounceMs, executeRefetch, isFetching, isFetchingNextPage, scheduleRefetch];
    $[15] = debounceMs;
    $[16] = executeRefetch;
    $[17] = isFetching;
    $[18] = isFetchingNextPage;
    $[19] = scheduleRefetch;
    $[20] = t8;
    $[21] = t9;
  } else {
    t8 = $[20];
    t9 = $[21];
  }
  useEffect(t8, t9);
  return requestRefetch;
}
function _temp() {}